import { useEffect, useRef, useState, useCallback } from "react";

/**
 * MagneticCursor — with click ripple effect
 *
 * Props:
 *  color       – base color               (default: "#40BBd2")
 *  size        – inner dot size in px     (default: 12)
 *  ringSize    – outer ring size in px    (default: 36)
 *  smoothness  – ring lag (0–1)           (default: 0.12)
 *  blendMode   – "difference" | "normal"  (default: "difference")
 */
export default function MagneticCursor({
  color = "#40BBd2",
  size = 12,
  ringSize = 36,
  smoothness = 0.12,
  blendMode = "difference",
}) {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const mouse = useRef({ x: 0, y: 0 });
  const ring = useRef({ x: 0, y: 0 });
  const rafId = useRef(null);
  const [visible, setVisible] = useState(false);
  const [ripples, setRipples] = useState([]);

  // Create a ripple at click position
  const spawnRipple = useCallback((x, y) => {
    const id = Date.now() + Math.random();
    setRipples((prev) => [...prev, { id, x, y }]);
    // Remove it after animation completes (600ms)
    setTimeout(() => {
      setRipples((prev) => prev.filter((r) => r.id !== id));
    }, 600);
  }, []);

  useEffect(() => {
    if (window.matchMedia("(hover: none)").matches) return;

    const onMove = (e) => {
      mouse.current = { x: e.clientX, y: e.clientY };
      if (!visible) setVisible(true);

      if (dotRef.current) {
        dotRef.current.style.transform = `translate(${e.clientX - size / 2}px, ${
          e.clientY - size / 2
        }px)`;
      }
    };

    const onClick = (e) => {
      spawnRipple(e.clientX, e.clientY);
    };

    const onLeave = () => setVisible(false);
    const onEnter = () => setVisible(true);

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);
    document.addEventListener("mousedown", onClick);

    const animate = () => {
      ring.current.x += (mouse.current.x - ring.current.x) * smoothness;
      ring.current.y += (mouse.current.y - ring.current.y) * smoothness;

      if (ringRef.current) {
        ringRef.current.style.transform = `translate(${
          ring.current.x - ringSize / 2
        }px, ${ring.current.y - ringSize / 2}px)`;
      }

      rafId.current = requestAnimationFrame(animate);
    };
    rafId.current = requestAnimationFrame(animate);

    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
      document.removeEventListener("mousedown", onClick);
      cancelAnimationFrame(rafId.current);
    };
  }, [size, ringSize, smoothness, visible, spawnRipple]);

  if (
    typeof window !== "undefined" &&
    window.matchMedia("(hover: none)").matches
  ) {
    return null;
  }

  return (
    <>
      <style>{`
        *, *::before, *::after { cursor: none !important; }

        @keyframes cf-ripple {
          0% {
            transform: translate(-50%, -50%) scale(0);
            opacity: 0.6;
          }
          100% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0;
          }
        }

        .cf-ripple {
          position: fixed;
          border-radius: 50%;
          pointer-events: none;
          z-index: 99997;
          animation: cf-ripple 0.6s cubic-bezier(0.2, 0.8, 0.4, 1) forwards;
        }
      `}</style>

      {/* Inner dot — snaps instantly */}
      <div
        ref={dotRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: size,
          height: size,
          borderRadius: "50%",
          background: color,
          pointerEvents: "none",
          zIndex: 99999,
          mixBlendMode: blendMode,
          opacity: visible ? 1 : 0,
          transition: "opacity 0.3s ease",
          willChange: "transform",
        }}
      />

      {/* Outer ring — lags behind */}
      <div
        ref={ringRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: ringSize,
          height: ringSize,
          borderRadius: "50%",
          border: `2px solid ${color}`,
          pointerEvents: "none",
          zIndex: 99998,
          mixBlendMode: blendMode,
          opacity: visible ? 1 : 0,
          transition: "opacity 0.3s ease",
          willChange: "transform",
        }}
      />

      {/* Ripples — one spawned per click, removed after 600ms */}
      {ripples.map((r) => (
        <div
          key={r.id}
          className="cf-ripple"
          style={{
            left: r.x,
            top: r.y,
            width: 70,
            height: 70,
            border: `8px solid ${color}`,
            mixBlendMode: blendMode,
          }}
        />
      ))}
    </>
  );
}